/* @flow */

import type { AVENUE_DATA_MODAL_TYPE } from '../../../components/modal/avenueModal/AvenueModal';
import type { CARD_DATA_MODAL_TYPE } from '../../../components/modal/cardModal/CardModalConstsAndTypes';
import type { EULA_DATA_MODAL_TYPE } from '../../../components/modal/eulaModal/EulaModal';
import type { GAME_PROFILE_SELECT_DATA_MODAL_TYPE } from '../../../components/modal/gameProfileSelectModal/GameProfileSelectModal';
import type { MESSAGE_MODAL_TYPE } from '../../../components/modal/messageModal/MessageModal';
import type { NEW_VERSION_DATA_MODAL_TYPE } from '../../../components/modal/newVersionModal/NewVersionModal';
import type { NPVR_DATA_MODAL_TYPE } from '../../../components/modal/npvrModal/NpvrModal';
import type { PIN_DATA_MODAL_TYPE } from '../../../components/modal/pinModal/PinModalConstsAndTypes';
import type { PRESENCE_CHECK_DATA_MODAL_TYPE } from '../../../components/modal/presenceCheckModal/PresenceCheckModal';
import type { VOD_PURCHASE_DATA_TYPE } from '../../../helpers/rights/pendingOperations';

import { type CONFIRMATION_DATA_MODAL_TYPE } from '../../../components/modal/confirmationModal/ConfirmationModal';
import {
  AUTHENTICATION_REQUIRED_MODAL,
  AVENUE_MODAL,
  CARD_MODAL,
  CONFIRMATION_MODAL,
  EULA_MODAL,
  EXTERNAL_CONTENT_MODAL,
  GAME_PROFILE_SELECT_MODAL,
  KEYBOARD_SHORTCUTS_MODAL,
  LEGAL_NOTICE_MODAL,
  MESSAGE_MODAL,
  NEW_VERSION_MODAL,
  NPVR_HELP_MODAL,
  NPVR_MODAL,
  PARENTAL_CONTROL_MODAL,
  PIN_MODAL,
  PRESENCE_CHECK_MODAL,
  PROFILES_MODAL,
  PROFILE_AVATAR_SELECT_MODAL,
  PROFILE_EDITOR_MODAL,
  PROFILE_SPLASHSCREEN_MODAL,
  SETTINGS_MODAL,
  VOD_PURCHASE_MODAL,
} from '../../../components/modal/modalTypes';
import { ModalActionType } from '../constants';

export type ModalAction =
  // Default action
  | { type: null }
  // Temporarily make all modals invisible
  | { type: typeof ModalActionType.Disable }
  // Make all modals visible again
  | { type: typeof ModalActionType.Enable }
  // Hide top most modal
  | {
      force?: boolean,
      type: typeof ModalActionType.Hide,
    }
  // Reopen last closed modal
  | { type: typeof ModalActionType.Reopen }
  // Save modal for a later reopening then hide it
  | {
      force?: boolean,
      type: typeof ModalActionType.SaveAndHide,
    }
  // Show authentication required modal
  | {
      modalType: typeof AUTHENTICATION_REQUIRED_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show avenue modal
  | {
      ariaLabel: string,
      avenueData?: AVENUE_DATA_MODAL_TYPE,
      modalType: typeof AVENUE_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show card modal
  | {
      cardData?: CARD_DATA_MODAL_TYPE,
      modalType: typeof CARD_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show confirmation modal
  | {
      ariaLabel: string,
      confirmationData: CONFIRMATION_DATA_MODAL_TYPE,
      modalId?: string,
      modalType: typeof CONFIRMATION_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show game profile selection
  | {
      gameProfileSelectData: GAME_PROFILE_SELECT_DATA_MODAL_TYPE,
      modalType: typeof GAME_PROFILE_SELECT_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show EULA modal
  | {
      eulaData: EULA_DATA_MODAL_TYPE,
      modalType: typeof EULA_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show external content modal
  | {
      ariaLabel: string,
      externalContentUrl: string,
      modalType: typeof EXTERNAL_CONTENT_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show message modal
  | {
      ariaLabel?: string,
      messageData: MESSAGE_MODAL_TYPE,
      modalType: typeof MESSAGE_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show keyboard shortcuts modal
  | {
      modalType: typeof KEYBOARD_SHORTCUTS_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show legal notice modal
  | {
      modalType: typeof LEGAL_NOTICE_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show new version modal
  | {
      newVersionData: NEW_VERSION_DATA_MODAL_TYPE,
      modalType: typeof NEW_VERSION_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show NPVR confirmation modal
  | {
      npvrData: NPVR_DATA_MODAL_TYPE,
      modalType: typeof NPVR_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show NPVR help modal
  | {
      modalType: typeof NPVR_HELP_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show parental control settings modal
  | {
      modalType: typeof PARENTAL_CONTROL_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show PIN code modal
  | {
      modalId?: string,
      modalType: typeof PIN_MODAL,
      pinData: PIN_DATA_MODAL_TYPE,
      type: typeof ModalActionType.Show,
    }
  // Show presence checker modal
  | {
      modalType: typeof PRESENCE_CHECK_MODAL,
      presenceCheckData: PRESENCE_CHECK_DATA_MODAL_TYPE,
      type: typeof ModalActionType.Show,
    }
  // Show profiles modal
  | {
      modalType: typeof PROFILES_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show profile editor modal
  | {
      modalId: string,
      modalType: typeof PROFILE_EDITOR_MODAL,
      profileId?: string,
      type: typeof ModalActionType.Show,
    }
  // Show avatar selector modal
  | {
      initialAvatarId: string | null,
      modalId: string,
      modalType: typeof PROFILE_AVATAR_SELECT_MODAL,
      selectedAvatarId: string | null,
      type: typeof ModalActionType.Show,
    }
  // Show profile splashscreen modal
  | {
      modalType: typeof PROFILE_SPLASHSCREEN_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show settings modal
  | {
      modalType: typeof SETTINGS_MODAL,
      type: typeof ModalActionType.Show,
    }
  // Show VOD purchase modal
  | {
      modalType: typeof VOD_PURCHASE_MODAL,
      type: typeof ModalActionType.Show,
      vodPurchaseData: VOD_PURCHASE_DATA_TYPE,
    };

const hideModal: (shouldBeReopenedLater?: boolean, force?: boolean) => ModalAction = (shouldBeReopenedLater, force) =>
  shouldBeReopenedLater
    ? {
        force,
        type: ModalActionType.SaveAndHide,
      }
    : {
        force,
        type: ModalActionType.Hide,
      };

const reopenModalCard: () => ModalAction = () => {
  return { type: ModalActionType.Reopen };
};

const showAuthenticationRequiredModal: () => ModalAction = () => {
  return {
    modalType: AUTHENTICATION_REQUIRED_MODAL,
    type: ModalActionType.Show,
  };
};

const showAvenueModal: (avenueData: AVENUE_DATA_MODAL_TYPE, ariaLabel: string) => ModalAction = (avenueData, ariaLabel) => {
  return {
    ariaLabel,
    avenueData,
    modalType: AVENUE_MODAL,
    type: ModalActionType.Show,
  };
};

const showCardModal: (cardData: CARD_DATA_MODAL_TYPE) => ModalAction = (cardData) => {
  return {
    cardData,
    modalType: CARD_MODAL,
    type: ModalActionType.Show,
  };
};

const showConfirmationModal: (confirmationData: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string, modalId?: string) => ModalAction = (confirmationData, ariaLabel, modalId) => {
  return {
    ariaLabel,
    confirmationData,
    modalId,
    modalType: CONFIRMATION_MODAL,
    type: ModalActionType.Show,
  };
};

const showGameProfileSelectModal: (gameProfileSelectData: GAME_PROFILE_SELECT_DATA_MODAL_TYPE) => ModalAction = (gameProfileSelectData) => {
  return {
    gameProfileSelectData,
    modalType: GAME_PROFILE_SELECT_MODAL,
    type: ModalActionType.Show,
  };
};

const showEulaModal: (eulaData: EULA_DATA_MODAL_TYPE) => ModalAction = (eulaData) => {
  return {
    eulaData,
    modalType: EULA_MODAL,
    type: ModalActionType.Show,
  };
};

const showExternalContentModal: (externalContentUrl: string, ariaLabel: string) => ModalAction = (externalContentUrl, ariaLabel) => {
  return {
    ariaLabel,
    externalContentUrl,
    modalType: EXTERNAL_CONTENT_MODAL,
    type: ModalActionType.Show,
  };
};

const showKeyboardShortcutsModal: () => ModalAction = () => {
  return {
    modalType: KEYBOARD_SHORTCUTS_MODAL,
    type: ModalActionType.Show,
  };
};

const showLegalNoticeModal: () => ModalAction = () => {
  return {
    modalType: LEGAL_NOTICE_MODAL,
    type: ModalActionType.Show,
  };
};

const showMessageModal: (messageData: MESSAGE_MODAL_TYPE, ariaLabel?: string) => ModalAction = (messageData, ariaLabel) => {
  return {
    ariaLabel,
    messageData,
    modalType: MESSAGE_MODAL,
    type: ModalActionType.Show,
  };
};

const showNewVersionModal: (newVersionData: NEW_VERSION_DATA_MODAL_TYPE) => ModalAction = (newVersionData) => {
  return {
    modalType: NEW_VERSION_MODAL,
    newVersionData,
    type: ModalActionType.Show,
  };
};

const showNpvrModal: (npvrData: NPVR_DATA_MODAL_TYPE) => ModalAction = (npvrData) => {
  return {
    modalType: NPVR_MODAL,
    npvrData,
    type: ModalActionType.Show,
  };
};

const showNpvrHelpModal: () => ModalAction = () => {
  return {
    modalType: NPVR_HELP_MODAL,
    type: ModalActionType.Show,
  };
};

const showParentalControlModal: () => ModalAction = () => {
  return {
    modalType: PARENTAL_CONTROL_MODAL,
    type: ModalActionType.Show,
  };
};

const showPinModal: (pinData: PIN_DATA_MODAL_TYPE, modalId?: string) => ModalAction = (pinData, modalId) => {
  return {
    modalId,
    modalType: PIN_MODAL,
    pinData,
    type: ModalActionType.Show,
  };
};

const showPresenceCheckModal: (presenceCheckData: PRESENCE_CHECK_DATA_MODAL_TYPE) => ModalAction = (presenceCheckData) => {
  return {
    modalType: PRESENCE_CHECK_MODAL,
    presenceCheckData,
    type: ModalActionType.Show,
  };
};

const showProfilesModal: () => ModalAction = () => {
  return {
    modalType: PROFILES_MODAL,
    type: ModalActionType.Show,
  };
};

const showProfileEditorModal: (modalId: string, profileId?: string) => ModalAction = (modalId, profileId) => {
  return {
    modalId,
    modalType: PROFILE_EDITOR_MODAL,
    profileId,
    type: ModalActionType.Show,
  };
};

const showProfileAvatarSelectModal: (selectedAvatarId: string | null, initialAvatarId: string | null, modalId: string) => ModalAction = (selectedAvatarId, initialAvatarId, modalId) => {
  return {
    initialAvatarId,
    modalId,
    modalType: PROFILE_AVATAR_SELECT_MODAL,
    selectedAvatarId,
    type: ModalActionType.Show,
  };
};

const showProfileSplashscreenModal: () => ModalAction = () => {
  return {
    modalType: PROFILE_SPLASHSCREEN_MODAL,
    type: ModalActionType.Show,
  };
};

const showSettingsModal: () => ModalAction = () => {
  return {
    modalType: SETTINGS_MODAL,
    type: ModalActionType.Show,
  };
};

const showVodPurchaseModal: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE) => ModalAction = (vodPurchaseData) => {
  return {
    modalType: VOD_PURCHASE_MODAL,
    type: ModalActionType.Show,
    vodPurchaseData,
  };
};

const enableModals: () => ModalAction = () => {
  return {
    type: ModalActionType.Enable,
  };
};

const disableModals: () => ModalAction = () => {
  return {
    type: ModalActionType.Disable,
  };
};

export {
  disableModals,
  enableModals,
  hideModal,
  reopenModalCard,
  showAuthenticationRequiredModal,
  showAvenueModal,
  showCardModal,
  showConfirmationModal,
  showEulaModal,
  showExternalContentModal,
  showGameProfileSelectModal,
  showKeyboardShortcutsModal,
  showLegalNoticeModal,
  showMessageModal,
  showNewVersionModal,
  showNpvrModal,
  showNpvrHelpModal,
  showParentalControlModal,
  showPinModal,
  showPresenceCheckModal,
  showProfileAvatarSelectModal,
  showProfileEditorModal,
  showProfilesModal,
  showProfileSplashscreenModal,
  showSettingsModal,
  showVodPurchaseModal,
};
